import api from './api';

export type PresenceStatus = 'online' | 'away' | 'offline';

export interface User {
  id: string;
  tenant_id?: string;
  partner_id?: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: string;
  phone?: string;
  timezone: string;
  status: string;
  presence_status?: PresenceStatus;
  mfa_enabled: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at?: string;
  roles: string[];
  permissions: string[];
  assigned_phone_number_id?: string;  // Telnyx phone number ID assigned to officer
  assigned_phone_number?: string;     // The actual Telnyx phone number
  dial_in_code?: string;              // Officer's personal dial-in access code for hearings
  external_id?: string;               // External ID for CRM integration
}

export interface UserCreateRequest {
  tenant_id?: string;
  partner_id?: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  user_type: string;
  phone?: string;
  timezone?: string;
  role_ids?: string[];
  assigned_phone_number_id?: string;  // Telnyx phone number for officer
  external_id?: string;               // External ID for CRM integration
}

export interface UserUpdateRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  timezone?: string;
  status?: string;
  role_ids?: string[];
  assigned_phone_number_id?: string | null;  // Telnyx phone number (null to unassign)
  dial_in_code?: string;  // Officer's personal dial-in access code for hearings
  external_id?: string;   // External ID for CRM integration
}

export interface AvailablePhoneNumber {
  id: string;
  number: string;
  formatted_number: string;
  friendly_name: string | null;
  number_type: string;
  is_available: boolean;
  assigned_to_user_id: string | null;
  assigned_to_user_name: string | null;
}

export interface UserListParams {
  skip?: number;
  limit?: number;
  tenant_id?: string;
  partner_id?: string;
  user_type?: string;
  status?: string;
  search?: string;
  // v2.2.47 — 'tree' returns the caller's own users PLUS users in
  // every direct sub-tenant. Read-only union. Default 'own' is the
  // pre-2.2.47 behavior.
  scope?: 'own' | 'tree';
}

export interface UserCountParams {
  tenant_id?: string;
  partner_id?: string;
}

export interface Role {
  id: string;
  tenant_id?: string;
  name: string;
  code: string;
  description?: string;
  is_system: boolean;
  permissions: string[];
}

export type SolutionCode = 'core' | 'va' | 'assist' | 'fax' | 'dars';

export interface Permission {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  solution: SolutionCode;
}

export interface CreateRoleRequest {
  name: string;
  code: string;
  description?: string;
  copy_permissions_from?: string;  // Role ID to copy permissions from
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
}

export interface CreatePermissionRequest {
  code: string;
  name: string;
  description?: string;
  category?: string;
}

export interface UpdatePermissionRequest {
  name?: string;
  description?: string;
  category?: string;
}

export interface PermissionCategory {
  category: string;
  permissions: Permission[];
}

export interface UserType {
  code: string;
  label: string;
}

export interface AvailableUserTypesResponse {
  user_types: UserType[];
  can_create_users: boolean;
}

export const userService = {
  /**
   * List users with optional filtering
   */
  async list(params: UserListParams = {}): Promise<User[]> {
    const response = await api.get('/api/v1/users', { params });
    return response.data;
  },

  /**
   * Get total user count with optional filtering
   */
  async getCount(params: UserCountParams = {}): Promise<number> {
    const response = await api.get('/api/v1/users/stats/count', { params });
    return response.data.count;
  },

  /**
   * Get the current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/v1/users/me');
    return response.data;
  },

  /**
   * Get a user by ID
   */
  async getById(userId: string): Promise<User> {
    const response = await api.get(`/api/v1/users/${userId}`);
    return response.data;
  },

  /**
   * Create a new user
   */
  async create(data: UserCreateRequest): Promise<User> {
    const response = await api.post('/api/v1/users', data);
    return response.data;
  },

  /**
   * Update a user
   */
  async update(userId: string, data: UserUpdateRequest): Promise<User> {
    const response = await api.put(`/api/v1/users/${userId}`, data);
    return response.data;
  },

  /**
   * Delete a user (soft delete)
   */
  async delete(userId: string): Promise<void> {
    await api.delete(`/api/v1/users/${userId}`);
  },

  /**
   * Reset user password (admin action)
   *
   * v2.4.42 (Ship 3, M-5) — response no longer carries a temporary
   * password. Backend sets `require_password_change=True` on the user;
   * they receive an email with the temp credentials and are forced
   * into the change-password flow on their next login.
   */
  async resetPassword(userId: string): Promise<{ message: string; email_sent: boolean; note: string }> {
    const response = await api.post(`/api/v1/users/${userId}/reset-password`);
    return response.data;
  },

  /**
   * Toggle user status (active/suspended)
   */
  async toggleStatus(userId: string): Promise<{ status: string }> {
    const response = await api.post(`/api/v1/users/${userId}/toggle-status`);
    return response.data;
  },

  /**
   * Get available user types for the current user to create
   */
  async getAvailableUserTypes(): Promise<AvailableUserTypesResponse> {
    const response = await api.get('/api/v1/users/types/available');
    return response.data;
  },

  /**
   * List available roles
   */
  async listRoles(includeSystem: boolean = true): Promise<Role[]> {
    const response = await api.get('/api/v1/roles', {
      params: { include_system: includeSystem },
    });
    return response.data;
  },

  /**
   * Get a role by ID
   */
  async getRole(roleId: string): Promise<Role> {
    const response = await api.get(`/api/v1/roles/${roleId}`);
    return response.data;
  },

  /**
   * List all permissions
   */
  async listPermissions(category?: string): Promise<Permission[]> {
    const response = await api.get('/api/v1/roles/permissions/all', {
      params: category ? { category } : undefined,
    });
    return response.data;
  },

  /**
   * List available phone numbers for assignment to officers
   */
  async listAvailablePhoneNumbers(): Promise<AvailablePhoneNumber[]> {
    const response = await api.get('/api/v1/users/phone-numbers/available');
    return response.data;
  },

  /**
   * Update user's presence status
   */
  async updatePresence(userId: string, presenceStatus: PresenceStatus): Promise<{ success: boolean; presence_status: PresenceStatus }> {
    const response = await api.put(`/api/v1/users/${userId}/presence`, null, {
      params: { presence_status: presenceStatus },
    });
    return response.data;
  },

  /**
   * Get user's current presence status
   */
  async getPresence(userId: string): Promise<{ presence_status: PresenceStatus }> {
    const response = await api.get(`/api/v1/users/${userId}/presence`);
    return response.data;
  },

  // ==================== Role Management ====================

  /**
   * Create a new role
   */
  async createRole(data: CreateRoleRequest): Promise<Role> {
    const response = await api.post('/api/v1/roles', data);
    return response.data;
  },

  /**
   * Update a role
   */
  async updateRole(roleId: string, data: UpdateRoleRequest): Promise<Role> {
    const response = await api.put(`/api/v1/roles/${roleId}`, data);
    return response.data;
  },

  /**
   * Delete a role
   */
  async deleteRole(roleId: string): Promise<void> {
    await api.delete(`/api/v1/roles/${roleId}`);
  },

  /**
   * Update role permissions
   */
  async updateRolePermissions(roleId: string, permissionCodes: string[]): Promise<Role> {
    const response = await api.put(`/api/v1/roles/${roleId}/permissions`, {
      permissions: permissionCodes,
    });
    return response.data;
  },

  /**
   * Get count of users in a role
   */
  async getRoleUserCount(roleId: string): Promise<{ count: number }> {
    // Phase 2.23.f — was `/user-count` (404'd); backend route is `/users/count`.
    const response = await api.get(`/api/v1/roles/${roleId}/users/count`);
    return response.data;
  },

  /**
   * Assign role to user
   */
  async assignRoleToUser(userId: string, roleId: string): Promise<void> {
    await api.post(`/api/v1/users/${userId}/roles/${roleId}`);
  },

  /**
   * Remove role from user
   */
  async removeRoleFromUser(userId: string, roleId: string): Promise<void> {
    await api.delete(`/api/v1/users/${userId}/roles/${roleId}`);
  },

  // ==================== Permission Management ====================

  /**
   * List permission categories
   */
  async listPermissionCategories(): Promise<PermissionCategory[]> {
    const response = await api.get('/api/v1/permissions/categories');
    return response.data;
  },

  /**
   * Create a permission
   */
  async createPermission(data: CreatePermissionRequest): Promise<Permission> {
    const response = await api.post('/api/v1/permissions', data);
    return response.data;
  },

  /**
   * Update a permission
   */
  async updatePermission(permissionId: string, data: UpdatePermissionRequest): Promise<Permission> {
    const response = await api.put(`/api/v1/permissions/${permissionId}`, data);
    return response.data;
  },

  /**
   * Delete a permission
   */
  async deletePermission(permissionId: string): Promise<void> {
    await api.delete(`/api/v1/permissions/${permissionId}`);
  },

  /**
   * Get count of roles using a permission
   */
  async getPermissionRoleCount(permissionId: string): Promise<{ count: number }> {
    const response = await api.get(`/api/v1/permissions/${permissionId}/roles/count`);
    return response.data;
  },
};

// Create roleService as an alias for role-related methods
export const roleService = {
  list: userService.listRoles,
  getById: userService.getRole,
  create: userService.createRole,
  update: userService.updateRole,
  delete: userService.deleteRole,
  updatePermissions: userService.updateRolePermissions,
  getUserCount: userService.getRoleUserCount,
  listPermissions: userService.listPermissions,
  listPermissionCategories: userService.listPermissionCategories,
  createPermission: userService.createPermission,
  updatePermission: userService.updatePermission,
  deletePermission: userService.deletePermission,
  getPermissionRoleCount: userService.getPermissionRoleCount,
};

export default userService;
